import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Sun, Moon, Smartphone } from "lucide-react";

function ModeBadge({ mode }) {
  const Icon = mode === "light" ? Sun : mode === "mobile" ? Smartphone : Moon;
  const color = mode === "light" ? "text-rogue-amber" : mode === "mobile" ? "text-rogue-cyan" : "text-geminii-muted";
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-mono ${color}`}>
      <Icon size={11} />
      {mode}
    </span>
  );
}

/**
 * Full-screen image lightbox with keyboard + click-outside dismiss
 * and prev/next navigation arrows. Used by ScreenshotCarousel and any
 * other screenshot consumer.
 */
export default function Lightbox({ open, items, index, onClose, onPrev, onNext }) {
  const handleKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    },
    [open, onClose, onPrev, onNext]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  useEffect(() => {
    if (open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [open]);

  const item = items?.[index];
  const total = items?.length || 0;

  return (
    <AnimatePresence>
      {open && item && (
        <motion.div
          data-testid="screenshot-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-10"
          onClick={onClose}
        >
          {/* Top bar */}
          <div
            className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 md:px-10 py-5 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <ModeBadge mode={item.mode || "dark"} />
              <span className="text-xs text-white/80 truncate max-w-[40vw]">{item.label || item.caption}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs font-mono text-white/60">{index + 1} / {total}</span>
              <button
                onClick={onClose}
                data-testid="lightbox-close"
                aria-label="Close"
                className="w-9 h-9 rounded-full flex items-center justify-center bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.1] text-white transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Prev */}
          {total > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPrev?.();
              }}
              data-testid="lightbox-prev"
              aria-label="Previous"
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center bg-white/[0.06] hover:bg-white/[0.14] border border-white/[0.1] text-white transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          {/* Next */}
          {total > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNext?.();
              }}
              data-testid="lightbox-next"
              aria-label="Next"
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center bg-white/[0.06] hover:bg-white/[0.14] border border-white/[0.1] text-white transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          )}

          {/* Image */}
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative max-w-[92vw] max-h-[82vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={item.src}
              alt={item.label || item.caption || "Screenshot"}
              className="max-w-full max-h-[82vh] rounded-xl border border-white/[0.08] shadow-2xl object-contain"
              data-testid="lightbox-image"
            />
          </motion.div>

          {/* Hint */}
          <div
            className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[11px] font-mono text-white/40 select-none"
            onClick={(e) => e.stopPropagation()}
          >
            esc to close · ← → to navigate
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
